import React, { useState, useEffect } from 'react'
import {
    useTheme,
    Typography,
    Button,
    Paper,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Grid,
    FormHelperText,
    CircularProgress,
    Box,
    Chip,
    TablePagination,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    IconButton,
} from '@material-ui/core'
import { SimpleTable, HeadCell, RowData } from '../../../components/Table'
import { Report, ReportTypes } from '../../../models/Analytics/types'
import { Container } from '../../../components/Container'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import GetAppIcon from '@material-ui/icons/GetApp'
import VisibilityIcon from '@material-ui/icons/Visibility'
import Tooltip from '@material-ui/core/Tooltip'
import { format } from 'date-fns'
import { useReports } from '../hooks/useReports'
import { useProperties } from '../hooks/useProperties'
import { CompanyWithInvoiceCode } from '../Property/types'
import { useOrganization } from '../../../hooks/useOrganization'
import PaginatedSearchSelect, {
    SelectOption,
} from '../../../components/PaginatedSearchSelect'

const useStyles = makeStyles((theme) => ({
    pageContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: theme.spacing(3, 4),
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    contentArea: {
        flex: 1,
        padding: theme.spacing(3, 4),
        overflow: 'auto', // This makes the content area scrollable
    },
    tableContainer: {
        height: '100%',
    },
    scrollableTableContainer: {
        maxHeight: 'calc(100vh - 250px)', // Adjust based on header and pagination height
        overflow: 'auto',
    },
    stickyPagination: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        zIndex: 1,
    },
    iconButton: {
        marginRight: theme.spacing(1),
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8, 0),
    },
    formControl: {
        marginBottom: theme.spacing(2),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    selectionChip: {
        margin: theme.spacing(0.5),
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        '& .MuiChip-deleteIcon': {
            color: theme.palette.primary.contrastText,
        },
    },
    chipContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
    },
    chipLabel: {
        marginRight: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    customPagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    paginationInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flex: 1,
    },
    pageButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginLeft: 'auto', // Push to the right
    },
    pageButton: {
        minWidth: 32,
        height: 32,
        padding: theme.spacing(0, 1),
        '&.active': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
    },
    rowsPerPageSelect: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}))

interface ReportRowData extends RowData {
    id: { value: number; sortValue: number }
    name: { value: string; sortValue: string }
    reportType: { value: string; sortValue: string }
    generatedBy: { value: string; sortValue: string }
    creationDate: { value: string; sortValue: number }
    actions: { value: JSX.Element; sortValue: string }
}

interface GenerateFormData {
    name: string
    organizationId?: number
    apartmentIds: number[]
}

// Filter properties by organization
const filterPropertiesByOrg = (
    properties: CompanyWithInvoiceCode[],
    orgId?: number,
) => {
    if (!orgId) return properties
    return properties.filter((property) => property.organization?.id === orgId)
}

// Map properties to SelectOption format
const mapPropertiesToOptions = (
    properties: CompanyWithInvoiceCode[],
): SelectOption[] => {
    return properties.map((property) => ({
        id: property.id,
        name: property.name,
        subtext: property.organization?.name,
    }))
}

// Map organizations to SelectOption format
const mapOrganizationsToOptions = (organizations: any[]): SelectOption[] => {
    return organizations.map((org) => ({
        id: org.id,
        name: org.name,
    }))
}

// Custom pagination component
const CustomPagination = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}: {
    count: number
    page: number
    rowsPerPage: number
    onPageChange: (page: number) => void
    onRowsPerPageChange: (rowsPerPage: number) => void
}) => {
    const classes = useStyles()
    const theme = useTheme()

    const totalPages = Math.ceil(count / rowsPerPage)
    const startItem = page * rowsPerPage + 1
    const endItem = Math.min((page + 1) * rowsPerPage, count)

    // Generate page numbers to show
    const getPageNumbers = () => {
        const pages = []
        const maxVisiblePages = 7

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 0; i < totalPages; i++) {
                pages.push(i)
            }
        } else {
            // Show first page, last page, current page, and pages around current
            pages.push(0) // First page

            if (page > 3) {
                pages.push(-1) // Ellipsis
            }

            const start = Math.max(1, page - 1)
            const end = Math.min(totalPages - 2, page + 1)

            for (let i = start; i <= end; i++) {
                pages.push(i)
            }

            if (page < totalPages - 4) {
                pages.push(-1) // Ellipsis
            }

            if (totalPages > 1) {
                pages.push(totalPages - 1) // Last page
            }
        }

        return pages
    }

    return (
        <div className={classes.customPagination}>
            <div className={classes.paginationInfo}>
                <Typography variant="body2" color="textSecondary">
                    Showing {startItem} to {endItem} of {count} results
                </Typography>

                <div className={classes.rowsPerPageSelect}>
                    <Typography variant="body2" color="textSecondary">
                        Rows per page:
                    </Typography>
                    <select
                        value={rowsPerPage}
                        onChange={(e) =>
                            onRowsPerPageChange(parseInt(e.target.value, 10))
                        }
                        style={{
                            padding: theme.spacing(0.5, 1),
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            <div className={classes.pageButtons}>
                <IconButton
                    size="small"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 0}
                    className={classes.pageButton}
                >
                    ‹
                </IconButton>

                {getPageNumbers().map((pageNum, index) => (
                    <IconButton
                        key={index}
                        size="small"
                        onClick={() => pageNum >= 0 && onPageChange(pageNum)}
                        disabled={pageNum === -1}
                        className={`${classes.pageButton} ${
                            pageNum === page ? 'active' : ''
                        }`}
                    >
                        {pageNum === -1 ? '...' : pageNum + 1}
                    </IconButton>
                ))}

                <IconButton
                    size="small"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    className={classes.pageButton}
                >
                    ›
                </IconButton>
            </div>
        </div>
    )
}

const AdminReportHome = () => {
    const theme = useTheme()
    const classes = useStyles()

    // Add pagination state
    const [page, setPage] = useState(0) // Material-UI uses 0-based indexing
    const [rowsPerPage, setRowsPerPage] = useState(20)

    const {
        reports,
        totalCount,
        hasNextPage,
        hasPreviousPage,
        currentPage,
        isLoading,
        generateReport: generateNewReport,
        isGenerating,
        downloadReport,
    } = useReports({
        page: page + 1, // Convert to 1-based indexing for API
        page_size: rowsPerPage,
    })

    const { propertyList, isLoading: isLoadingProperties } = useProperties()

    const { organizationList, getOrganizationList } = useOrganization()

    const properties = propertyList?.data || []

    const [openGenerateForm, setOpenGenerateForm] = useState(false)
    const [formData, setFormData] = useState<GenerateFormData>({
        name: 'Account Manager Audit Report',
        apartmentIds: [],
    })
    const [formErrors, setFormErrors] = useState<
        Partial<Record<keyof GenerateFormData, string>>
    >({})
    const [availableProperties, setAvailableProperties] = useState<
        CompanyWithInvoiceCode[]
    >([])
    const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)

    // Fetch organizations on component mount
    useEffect(() => {
        const loadOrganizations = async () => {
            if (organizationList.length === 0) {
                setIsLoadingOrganizations(true)
                await getOrganizationList({ detail: true })
                setIsLoadingOrganizations(false)
            }
        }
        loadOrganizations()
    }, [])

    // Update available properties when organization changes or properties load
    useEffect(() => {
        if (properties.length > 0) {
            setAvailableProperties(
                filterPropertiesByOrg(properties, formData.organizationId),
            )
        }
    }, [formData.organizationId, properties])

    // Reset selected properties when available properties change
    useEffect(() => {
        if (formData.organizationId) {
            // Keep only the apartment IDs that are still in the available properties
            const validIds = formData.apartmentIds.filter((id) =>
                availableProperties.some((p) => p.id === id),
            )
            if (validIds.length !== formData.apartmentIds.length) {
                setFormData((prev) => ({
                    ...prev,
                    apartmentIds: validIds,
                }))
            }
        }
    }, [availableProperties, formData.apartmentIds, formData.organizationId])

    const headCells: HeadCell<ReportRowData>[] = [
        {
            id: 'id',
            label: 'ID',
            align: 'left',
        },
        {
            id: 'name',
            label: 'Report Name',
            align: 'left',
        },
        {
            id: 'reportType',
            label: 'Report Type',
            align: 'left',
        },
        {
            id: 'generatedBy',
            label: 'Generated By',
            align: 'left',
        },
        {
            id: 'creationDate',
            label: 'Creation Date',
            align: 'left',
        },
        {
            id: 'actions',
            label: 'Actions',
            align: 'center',
            disablePadding: false,
        },
    ]

    const formatReportType = (type: string): string => {
        return type
            .split('_')
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ')
    }

    const handleOpenGenerateForm = () => {
        setOpenGenerateForm(true)
    }

    const handleCloseGenerateForm = () => {
        setOpenGenerateForm(false)
    }

    const handleFormChange = (field: keyof GenerateFormData, value: any) => {
        console.log(`Setting ${field} to:`, value)

        if (field === 'apartmentIds' && !Array.isArray(value)) {
            value = [] // Ensure apartment IDs is always an array
        }

        if (field === 'organizationId') {
            if (value === '' || value === null) {
                value = undefined // Handle empty string or null as undefined
                // Reset apartmentIds when organization is cleared
                setFormData((prev) => ({
                    ...prev,
                    [field]: value,
                    apartmentIds: [],
                }))

                // Clear error when field is updated
                if (formErrors[field]) {
                    setFormErrors({
                        ...formErrors,
                        [field]: undefined,
                    })
                }
                return
            }
        }

        setFormData({
            ...formData,
            [field]: value,
        })

        // Clear error when field is updated
        if (formErrors[field]) {
            setFormErrors({
                ...formErrors,
                [field]: undefined,
            })
        }
    }

    const validateForm = (): boolean => {
        const errors: Partial<Record<keyof GenerateFormData, string>> = {}

        // No validation needed for optional fields

        setFormErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleGenerateReport = () => {
        if (validateForm()) {
            generateNewReport({
                name: formData.name || 'Account Manager Audit Report',
                report_type: ReportTypes.ACCOUNT_MANAGER_AUDIT_REPORT,
                filters: {
                    organization_id: formData.organizationId,
                    apartment_ids:
                        formData.apartmentIds.length > 0
                            ? formData.apartmentIds
                            : undefined,
                },
            })
            handleCloseGenerateForm()
        }
    }

    const handleViewReport = (report: Report) => {
        if (report.xlsx_report) {
            // Open Excel file in Office Online viewer in a new tab
            const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                report.xlsx_report,
            )}`
            window.open(viewerUrl, '_blank')
        } else {
            console.log('No XLSX report available to view')
        }
    }

    const renderActionButtons = (report: Report) => (
        <>
            <Tooltip title="View Report">
                <IconButton
                    className={classes.iconButton}
                    size="small"
                    onClick={() => handleViewReport(report)}
                    disabled={!report.xlsx_report}
                >
                    <VisibilityIcon />
                </IconButton>
            </Tooltip>
            <Tooltip
                title={`Download Report (${
                    report.xlsx_report ? 'XLSX' : 'PDF'
                })`}
            >
                <IconButton
                    className={classes.iconButton}
                    size="small"
                    onClick={() => downloadReport(report)}
                    disabled={!report.xlsx_report}
                >
                    <GetAppIcon />
                </IconButton>
            </Tooltip>
        </>
    )

    const formatDate = (dateString: string) => {
        return format(new Date(dateString), 'MMM d, yyyy h:mm a')
    }

    // Filter reports to only show Account Manager Audit Reports
    const filteredReports = reports.filter(
        (report) =>
            report.report_type === ReportTypes.ACCOUNT_MANAGER_AUDIT_REPORT,
    )

    const handleChangePage = (newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage)
        setPage(0) // Reset to first page when changing rows per page
    }

    const renderTableContent = () => {
        return (
            <>
                <Paper>
                    <div className={classes.scrollableTableContainer}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Report Name</TableCell>
                                    <TableCell>Report Type</TableCell>
                                    <TableCell>Generated By</TableCell>
                                    <TableCell>Creation Date</TableCell>
                                    <TableCell align="center">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <CircularProgress size={24} />
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    marginTop: theme.spacing(1),
                                                }}
                                            >
                                                Loading reports...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredReports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="h6">
                                                No audit reports found
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="textSecondary"
                                                style={{
                                                    marginTop: theme.spacing(1),
                                                }}
                                            >
                                                Generate a new report to get
                                                started
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredReports.map((report) => (
                                        <TableRow key={report.id}>
                                            <TableCell>{report.id}</TableCell>
                                            <TableCell>{report.name}</TableCell>
                                            <TableCell>
                                                {formatReportType(
                                                    report.report_type,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {report.generated_by.name}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(
                                                    report.creation_time,
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {renderActionButtons(report)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className={classes.stickyPagination}>
                        <CustomPagination
                            count={totalCount}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </div>
                </Paper>
            </>
        )
    }

    return (
        <Container className={classes.pageContainer}>
            <div className={classes.header}>
                <div className={classes.headerContent}>
                    <Typography variant="h4">
                        Account Manager Audit Reports
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenGenerateForm}
                        disabled={isGenerating}
                    >
                        Generate New Audit Report
                    </Button>
                </div>
            </div>

            <Divider />

            <div className={classes.contentArea}>
                <Paper>
                    <div className={classes.tableContainer}>
                        {renderTableContent()}
                    </div>
                </Paper>
            </div>

            {/* Generate Report Form Dialog */}
            <Dialog
                open={openGenerateForm}
                onClose={handleCloseGenerateForm}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Generate Account Manager Audit Report</DialogTitle>
                <DialogContent>
                    {isLoadingProperties || isLoadingOrganizations ? (
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl
                                    fullWidth
                                    className={classes.formControl}
                                >
                                    <TextField
                                        label="Report Name (Optional)"
                                        placeholder="Account Manager Audit Report"
                                        value={formData.name}
                                        onChange={(e) =>
                                            handleFormChange(
                                                'name',
                                                e.target.value,
                                            )
                                        }
                                    />
                                    <FormHelperText>
                                        Leave blank to use default name
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <div className={classes.formControl}>
                                    <PaginatedSearchSelect
                                        options={mapOrganizationsToOptions(
                                            organizationList,
                                        )}
                                        value={formData.organizationId || null}
                                        onChange={(value) => {
                                            console.log(
                                                'Organization selection changed:',
                                                value,
                                            )
                                            handleFormChange(
                                                'organizationId',
                                                value,
                                            )
                                        }}
                                        label="Organization (Optional)"
                                        placeholder="All Organizations"
                                        helperText="Filter report to a specific organization"
                                    />
                                    {formData.organizationId && (
                                        <div className={classes.chipContainer}>
                                            <Typography
                                                variant="body2"
                                                className={classes.chipLabel}
                                            >
                                                Selected organization:
                                            </Typography>
                                            <Chip
                                                label={
                                                    organizationList.find(
                                                        (org) =>
                                                            org.id ===
                                                            formData.organizationId,
                                                    )?.name ||
                                                    'Selected Organization'
                                                }
                                                onDelete={() =>
                                                    handleFormChange(
                                                        'organizationId',
                                                        null,
                                                    )
                                                }
                                                size="small"
                                                className={
                                                    classes.selectionChip
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </Grid>

                            <Grid item xs={12}>
                                <div className={classes.formControl}>
                                    <PaginatedSearchSelect
                                        options={mapPropertiesToOptions(
                                            availableProperties,
                                        )}
                                        value={formData.apartmentIds}
                                        onChange={(value) => {
                                            console.log(
                                                'Property selection changed:',
                                                value,
                                            )
                                            handleFormChange(
                                                'apartmentIds',
                                                Array.isArray(value)
                                                    ? value
                                                    : [],
                                            )
                                        }}
                                        label="Properties (Optional)"
                                        placeholder="Search properties..."
                                        multiple
                                        disabled={
                                            availableProperties.length === 0
                                        }
                                        helperText={
                                            formData.organizationId
                                                ? 'Select specific properties to include in the report'
                                                : 'Select an organization first to filter properties, or select from all properties'
                                        }
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGenerateForm} color="default">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerateReport}
                        color="primary"
                        variant="contained"
                        disabled={
                            isGenerating ||
                            isLoadingProperties ||
                            isLoadingOrganizations
                        }
                    >
                        Generate Report
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default AdminReportHome
